import { useState } from "react";
import { IoAddOutline } from "react-icons/io5";
import { TbDatabaseCog } from "react-icons/tb";
import { useNavigate } from "react-router";
import { useGlobalStore } from "~/stores/store-provider";
import RenameModal from "./sidebar-components/rename-modal";
import DeleteModal from "./sidebar-components/delete-modal";
import ProfileDropDown from "./sidebar-components/profile-drop-down";
import SidebarHeader from "./sidebar-components/sidebar-header";
import ProjectList from "./sidebar-components/project-list";
import PriceComparison from "~/components/price-comparison/price-comparison";

type SessionData = {
  sessionId: string | null;
  sessionTitle: string | null;
};

const Sidebar = () => {
  const navigate = useNavigate();
  const {
    collapsed,
    toggleCollapse,
    renameSession,
    deleteSession,
    clearChatMessages,
    resetAnalysisDocument,
    setSessionDocumentView,
  } = useGlobalStore((state) => state);

  const [renameActiveModal, setRenameActiveModal] = useState<boolean>(false);
  const [deleteActiveModal, setDeleteActiveModal] = useState<boolean>(false);
  const [renameModalProps, setRenameModalProps] = useState<SessionData>({
    sessionId: null,
    sessionTitle: null,
  });
  const [billingModalOpen, setBillingModalOpen] = useState<boolean>(false);

  const setActiveRenameModalHandler = (isActive: boolean) => {
    setRenameActiveModal(isActive);
  };

  const setActiveDeleteModalHandler = (isActive: boolean) => {
    setDeleteActiveModal(isActive);
  };

  const setRenameModalPropsHandler = (sessionData: SessionData) => {
    setRenameModalProps({
      sessionId: sessionData.sessionId,
      sessionTitle: sessionData.sessionTitle,
    });
  };

  const renameSessionHandler = (
    sessionId: string | null,
    sessionTitle: string | null
  ) => {
    renameSession(sessionId, sessionTitle);
  };

  const deleteSessionHandler = async (sessionId: string | null) => {
    if (sessionId) {
      await deleteSession(sessionId);
    }
  };

  const handleNewProject = () => {
    // Clear any existing session state
    clearChatMessages();
    resetAnalysisDocument();
    setSessionDocumentView(false);

    // Navigate to new session page
    navigate("/session");
  };

  const handleDataConnections = () => {
    // Navigate to connections page
    navigate("/connections");
  };

  return (
    <div
      className={`flex flex-col h-screen bg-base-200 transition-all duration-150 ${
        collapsed ? "w-12" : "w-78"
      }`}
    >
      {/* Top Header */}
      <SidebarHeader collapsed={collapsed} toggleCollapse={toggleCollapse} />
      {/* New Project Button */}
      <div className="flex justify-center p-4 pb-2">
        {collapsed ? (
          <button
            onClick={handleNewProject}
            className="btn btn-primary btn-soft btn-square btn-md"
            title="New Project"
          >
            <IoAddOutline size={18} />
          </button>
        ) : (
          <button
            onClick={handleNewProject}
            className="btn btn-dash btn-primary btn-md btn-block"
          >
            <IoAddOutline size={18} />
            New Project
          </button>
        )}
      </div>

      {/* Data Connections Button */}
      <div className="flex justify-center px-4 pb-4">
        {collapsed ? (
          <button
            onClick={handleDataConnections}
            className="btn btn-primary btn-soft btn-square btn-md mt-2"
            title="Data Connections"
          >
            <TbDatabaseCog size={18} />
          </button>
        ) : (
          <button
            onClick={handleDataConnections}
            className="btn btn-primary btn-soft btn-md btn-block justify-center mt-2"
          >
            <TbDatabaseCog size={18} />
            Data Connections
          </button>
        )}
      </div>

      {/* Projects Section / Spacer */}
      <div className="flex-1 p-1 w-full overflow-auto">
        {!collapsed && (
          <ProjectList
            setActiveRenameModalHandler={setActiveRenameModalHandler}
            setActiveDeleteModalHandler={setActiveDeleteModalHandler}
            setRenameModalPropsHandler={setRenameModalPropsHandler}
          />
        )}
      </div>

      {/* Profile Section */}
      <div className={`cursor-pointer mb-2 p-2 ${collapsed ? "w-12" : "w-78"}`}>
        <ProfileDropDown
          collapsed={collapsed}
          setBillingModalOpen={setBillingModalOpen}
        />
      </div>

      {/* Modals */}
      <RenameModal
        renameActiveModal={renameActiveModal}
        renameModalProps={renameModalProps}
        setActiveRenameModalHandler={setActiveRenameModalHandler}
        renameSessionHandler={renameSessionHandler}
      />
      <DeleteModal
        renameModalProps={renameModalProps}
        deleteActiveModal={deleteActiveModal}
        setActiveDeleteModalHandler={setActiveDeleteModalHandler}
        deleteSessionHandler={deleteSessionHandler}
      />

      <PriceComparison
        billingModalOpen={billingModalOpen}
        setBillingModalOpen={setBillingModalOpen}
      />
    </div>
  );
};

export default Sidebar;
